import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchCampaigns,
  fetchCampaign,
  deleteCampaignById,
  sendCampaignMail,
  resetDetail,
  CampaignStatus,
  CampaignStatusValue,
} from "@/store/altingross/campaigns";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Send,
  Eye,
  Mail,
  TrendingUp,
  Clock,
  CheckCircle2,
  FileEdit,
  Calendar,
  MoreHorizontal,
  Trash2,
  Plus,
  Loader2,
  Users,
  LayoutList,
  Copy,
  Tag,
  User,
  AtSign,
  Hash,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ─── Helpers ───────────────────────────────────────────────────────────────
const statusConfig = {
  Taslak:       { class: "bg-slate-100 text-slate-600 border-slate-200", icon: FileEdit },
  Gönderildi:   { class: "bg-green-50 text-green-700 border-green-200",  icon: CheckCircle2 },
  Gönderiliyor: { class: "bg-sky-50 text-sky-700 border-sky-200",        icon: Clock },
  "İptal Edildi": { class: "bg-red-50 text-red-600 border-red-200",      icon: X },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("tr-TR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const parseTags = (tags) =>
  tags ? tags.split(",").map((t) => t.trim()).filter(Boolean) : [];

const PAGE_SIZE = 10;

// ─── Component ─────────────────────────────────────────────────────────────
export default function Campaigns() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    list,
    detail,
    sendLoading, sendingId,
    deleteLoading, deletingId,
  } = useSelector((s) => s.campaigns);

  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("Tümü");
  const [page, setPage]                 = useState(1);
  const [detailOpen, setDetailOpen]     = useState(false);

  const buildFilter = useCallback(() => {
    if (statusFilter !== "Tümü") {
      return { field: "status", value: String(CampaignStatusValue[statusFilter]), operator: "Equals" };
    }
    if (search.trim()) {
      return { field: "name", value: search.trim(), operator: "Contains" };
    }
    return null;
  }, [statusFilter, search]);

  useEffect(() => {
    dispatch(fetchCampaigns({ page, size: PAGE_SIZE, filter: buildFilter() }));
  }, [dispatch, page, statusFilter]);

  const items      = list.data?.items ?? [];
  const totalPages = list.data?.totalPages ?? 1;
  const totalCount = list.data?.count ?? 0;

  const stats = useMemo(() => ({
    totalRecipients: items.reduce((a, c) => a + (c.numberOfPeopleSent ?? 0), 0),
    sent:      items.filter((c) => c.status === CampaignStatusValue.Gönderildi).length,
    draft:     items.filter((c) => c.status === CampaignStatusValue.Taslak).length,
    cancelled: items.filter((c) => c.status === CampaignStatusValue.İptalEdildi).length,
  }), [items]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setStatusFilter("Tümü");
    setPage(1);
    dispatch(fetchCampaigns({
      page: 1,
      size: PAGE_SIZE,
      filter: search.trim() ? { field: "name", value: search.trim(), operator: "Contains" } : null,
    }));
  };

  const handleStatusFilter = (s) => {
    setStatusFilter(s);
    setSearch("");
    setPage(1);
  };

  const handleOpenDetail = (id) => {
    dispatch(fetchCampaign(id));
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    dispatch(resetDetail());
  };

  const handleDelete = (id) => {
    dispatch(deleteCampaignById(id))
      .unwrap()
      .then(() => toast.success("Kampanya silindi"))
      .catch(() => toast.error("Kampanya silinirken hata oluştu"));
  };

  const handleSendMail = (id) => {
    dispatch(sendCampaignMail(id))
      .unwrap()
      .then(() => {
        toast.success("Kampanya başarıyla gönderildi");
        dispatch(fetchCampaigns({ page, size: PAGE_SIZE }));
      })
      .catch(() => toast.error("Kampanya gönderilirken hata oluştu"));
  };

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        testId="campaigns-header"
        title="Kampanyalar"
        description="Tüm e-posta kampanyalarınızı tek panelden yönetin, performansı izleyin."
        action={
          <Button
            data-testid="campaigns-new-button"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm shadow-blue-200"
            onClick={() => toast.info("Yeni kampanya akışı: Bülten Aboneleri → Mail Kampanyası Gönder")}
          >
            <Plus className="h-4 w-4 mr-1.5" /> Yeni Kampanya
          </Button>
        }
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Toplam Kampanya"  value={totalCount}                                       icon={LayoutList}  accent="blue"   sub="Tüm kayıtlar"             testId="kpi-total"      />
        <KpiCard label="Toplam Alıcı"     value={stats.totalRecipients.toLocaleString("tr-TR")}    icon={Users}       accent="sky"    sub="Bu sayfadaki kampanyalar" testId="kpi-recipients" />
        <KpiCard label="Gönderildi"       value={stats.sent}                                       icon={CheckCircle2} accent="green" sub="Bu sayfada"               testId="kpi-sent"       />
        <KpiCard label="Taslak"           value={stats.draft}                                      icon={FileEdit}    accent="slate"  sub="Yayına hazırlanıyor"      testId="kpi-draft"      />
      </div>

      {/* Table */}
      <div className="glass-card p-6">
        <form onSubmit={handleSearchSubmit} className="flex gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Kampanya adı ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="campaigns-search-input"
              className="pl-10 h-10 bg-white rounded-md"
            />
          </div>
          <Button type="submit" variant="outline" className="h-10 rounded-md">
            Ara
          </Button>
        </form>

        {list.loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Yükleniyor...
          </div>
        ) : list.error ? (
          <div className="text-center py-16 text-red-500 text-sm">
            Veriler yüklenirken hata oluştu.
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-100">
                  <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Kampanya</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Gönderen</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 text-right">Alıcı</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Tarih</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Durum</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                      Kampanya bulunamadı
                    </TableCell>
                  </TableRow>
                ) : items.map((c) => {
                  const statusLabel = CampaignStatus[c.status] ?? "Taslak";
                  const cfg  = statusConfig[statusLabel];
                  const Icon = cfg.icon;
                  const tags = parseTags(c.tags);
                  const isSending = sendLoading && sendingId === c.id;
                  return (
                    <TableRow
                      key={c.id}
                      data-testid={`campaign-row-${c.id}`}
                      className={cn(
                        "border-slate-100 transition-opacity",
                        sendLoading
                          ? "opacity-50 pointer-events-none select-none"
                          : "hover:bg-blue-50/40",
                        isSending && "opacity-100 bg-blue-50/60"
                      )}
                    >
                      <TableCell>
                        <div className="leading-tight">
                          <div className="font-semibold text-slate-900 flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                            {c.name}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>{c.subject}</span>
                            {tags.map((t) => (
                              <span key={t} className="px-1.5 h-4 inline-flex items-center rounded text-[10px] font-medium bg-blue-50 text-blue-700">#{t}</span>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="leading-tight">
                          <div className="text-sm font-medium text-slate-800">{c.senderName || "—"}</div>
                          <div className="text-xs text-slate-400">{c.senderEmail || ""}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-800 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {c.numberOfPeopleSent > 0 ? c.numberOfPeopleSent.toLocaleString("tr-TR") : "—"}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 whitespace-nowrap">
                        {c.status === CampaignStatusValue.Gönderildi ? formatDate(c.sentAt) : formatDate(c.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${cfg.class} font-medium`}>
                          <Icon className="h-3 w-3 mr-1" />
                          {statusLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {isSending ? (
                          <div className="flex h-8 w-8 items-center justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                          </div>
                        ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 hover:bg-blue-50" data-testid={`campaign-actions-${c.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleOpenDetail(c.id)}>
                              <Eye className="h-3.5 w-3.5 mr-2" /> Detayları Gör
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/newsletter/campaign/${c.id}`)}>
                              <Copy className="h-3.5 w-3.5 mr-2" /> Kopyala
                            </DropdownMenuItem>
                            {c.status === CampaignStatusValue.Taslak && (
                              <DropdownMenuItem onClick={() => toast.info("Düzenleme akışı yakında")}>
                                <FileEdit className="h-3.5 w-3.5 mr-2" /> Düzenle
                              </DropdownMenuItem>
                            )}
                            {c.status !== CampaignStatusValue.İptalEdildi && (
                              <DropdownMenuItem
                                disabled={sendLoading && sendingId === c.id}
                                onClick={() => handleSendMail(c.id)}
                              >
                                {sendLoading && sendingId === c.id
                                  ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                  : <Mail className="h-3.5 w-3.5 mr-2" />}
                                Mail olarak gönder
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600 focus:bg-red-50 focus:text-red-700"
                              disabled={deleteLoading && deletingId === c.id}
                              onClick={() => handleDelete(c.id)}
                            >
                              {deleteLoading && deletingId === c.id
                                ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5 mr-2" />}
                              Sil
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
                <div className="text-xs text-slate-500">
                  Sayfa <span className="font-semibold text-slate-700">{page}</span> / <span className="font-semibold text-slate-700">{totalPages}</span> — {totalCount} kampanya
                </div>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" disabled={page === 1 || list.loading} onClick={() => setPage((p) => p - 1)} data-testid="campaigns-prev-page" className="rounded-md">
                    Önceki
                  </Button>
                  {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button key={pageNum} size="sm" variant={pageNum === page ? "default" : "outline"} onClick={() => setPage(pageNum)}
                        className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 rounded-md h-9 w-9 p-0" : "rounded-md h-9 w-9 p-0"}>
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" disabled={page === totalPages || list.loading} onClick={() => setPage((p) => p + 1)} data-testid="campaigns-next-page" className="rounded-md">
                    Sonraki
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Sheet */}
      <CampaignDetailSheet open={detailOpen} onClose={handleCloseDetail} detail={detail} />
    </div>
  );
}

// ─── Detail Sheet ──────────────────────────────────────────────────────────
function CampaignDetailSheet({ open, onClose, detail }) {
  const c = detail.data;
  const statusLabel = c ? (CampaignStatus[c.status] ?? "Taslak") : null;
  const cfg = statusLabel ? statusConfig[statusLabel] : null;
  const StatusIcon = cfg?.icon;
  const tags = c ? parseTags(c.tags) : [];

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base font-semibold text-slate-900 leading-tight truncate">
                {detail.loading ? "Yükleniyor..." : (c?.name ?? "Kampanya Detayı")}
              </SheetTitle>
              {c?.subject && (
                <p className="text-sm text-slate-500 mt-0.5 truncate">{c.subject}</p>
              )}
            </div>
            {cfg && statusLabel && (
              <Badge variant="outline" className={`${cfg.class} font-medium shrink-0`}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusLabel}
              </Badge>
            )}
          </div>
        </SheetHeader>

        {detail.loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Yükleniyor...
          </div>
        ) : detail.error ? (
          <div className="text-center py-20 text-red-500 text-sm px-6">
            Detaylar yüklenirken hata oluştu.
          </div>
        ) : c ? (
          <div className="px-6 py-5 space-y-6">

            {/* Gönderen bilgileri */}
            <Section title="Gönderen Bilgileri">
              <InfoRow icon={User}   label="Gönderen Adı"  value={c.senderName  || "—"} />
              <InfoRow icon={AtSign} label="Gönderen E-posta" value={c.senderEmail || "—"} />
            </Section>

            <Separator className="bg-slate-100" />

            {/* Kampanya bilgileri */}
            <Section title="Kampanya Bilgileri">
              <InfoRow icon={Mail}     label="Konu"         value={c.subject     || "—"} />
              {c.previewText && (
                <InfoRow icon={Eye}    label="Önizleme Metni" value={c.previewText} />
              )}
              <InfoRow icon={Users}       label="Gönderilen Kişi Sayısı"   value={c.numberOfPeopleSent > 0 ? c.numberOfPeopleSent.toLocaleString("tr-TR") : "—"} />
              <InfoRow icon={Send}        label="Toplam Gönderim Sayısı"   value={c.sendCount > 0 ? c.sendCount : "—"} />
              <InfoRow icon={CheckCircle2} label="Yalnızca Onaylananlar"   value={c.isOnlyForVerifiedEmails ? "Evet — Sadece onaylı aboneler" : "Hayır — Tüm aboneler"} />
            </Section>

            {/* Etiketler */}
            {tags.length > 0 && (
              <>
                <Separator className="bg-slate-100" />
                <Section title="Etiketler">
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                        <Hash className="h-3 w-3" />{t}
                      </span>
                    ))}
                  </div>
                </Section>
              </>
            )}

            <Separator className="bg-slate-100" />

            {/* Tarihler */}
            <Section title="Tarihler">
              <InfoRow icon={Calendar}     label="Oluşturulma"  value={formatDate(c.createdAt)} />
              <InfoRow icon={Calendar}     label="Güncellenme"  value={formatDate(c.updatedAt)} />
              {c.status === CampaignStatusValue.Gönderildi && (
                <InfoRow icon={CheckCircle2} label="Gönderilme" value={formatDate(c.sentAt)} />
              )}
              {c.isDeleted && c.deletedAt && (
                <InfoRow icon={Trash2} label="Silinme" value={formatDate(c.deletedAt)} />
              )}
            </Section>

            {/* AI Prompt & Response */}
            {(c.groqAiPrompt || c.groqAiResponse) && (
              <>
                <Separator className="bg-slate-100" />
                <Section title="Yapay Zeka">
                  {c.groqAiPrompt && (
                    <div className="space-y-1">
                      <span className="text-xs text-slate-400">Prompt</span>
                      <p className="text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-2.5 leading-relaxed whitespace-pre-wrap">
                        {c.groqAiPrompt}
                      </p>
                    </div>
                  )}
                  {c.groqAiResponse && (
                    <div className="space-y-1 mt-3">
                      <span className="text-xs text-slate-400">Yanıt</span>
                      <p className="text-sm text-slate-600 bg-indigo-50 rounded-md px-3 py-2.5 leading-relaxed whitespace-pre-wrap">
                        {c.groqAiResponse}
                      </p>
                    </div>
                  )}
                </Section>
              </>
            )}

            {/* HTML İçerik Önizleme */}
            {c.htmlContent && (
              <>
                <Separator className="bg-slate-100" />
                <Section title="Mail İçeriği Önizleme">
                  <div className="rounded-md border border-slate-200 overflow-hidden">
                    <iframe
                      srcDoc={c.htmlContent}
                      title="Kampanya Önizleme"
                      sandbox="allow-same-origin"
                      className="w-full h-[420px] bg-white"
                    />
                  </div>
                </Section>
              </>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// ─── Alt bileşenler ────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">{title}</p>
      {children}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs text-slate-400 block">{label}</span>
        <span className="text-sm text-slate-800 font-medium break-words">{value}</span>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent, sub, testId }) {
  const accentMap = {
    blue:   "bg-blue-50 text-blue-600",
    sky:    "bg-sky-50 text-sky-600",
    indigo: "bg-indigo-50 text-indigo-600",
    slate:  "bg-slate-100 text-slate-700",
  };
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
        <div className={cn("h-9 w-9 rounded-md flex items-center justify-center", accentMap[accent])}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="metric-number text-3xl mt-2">{value}</div>
      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
        <TrendingUp className="h-3 w-3 text-blue-500" />
        {sub}
      </div>
    </div>
  );
}
